/**
 * What an unknown path lands on (FR-014, contracts/web-surface.md §1).
 *
 * Rails' catch-all serves the same SPA shell for every non-XHR HTML path, so
 * the server never renders a not-found page of its own and the router's
 * `defaultNotFoundComponent` — wired in `entrypoints/application.tsx` — is what
 * a visitor actually sees. It renders through the root route's `<Outlet />`,
 * which is why the header, the Vault background and the loaded typefaces are
 * already here and this file only has to supply the body.
 *
 * ## Three elements, and the same restraint as the hero
 *
 * A heading, a sentence, and a link home. There is deliberately no hero and no
 * signup form: the marketing page is where the page asks for something, and
 * repeating the ask on a dead end reads as opportunism rather than help. It
 * also keeps this route to a render-only cost, which is the bound FR-021(d)
 * holds it to.
 *
 * The tone follows the rest of the site — plain, unapologetic, no joke and no
 * illustration. Someone who mistyped an address wants the way back, not wit.
 *
 * ## This page's one `<h1>` (FR-019(e))
 *
 * The hero's wordmark plays this part on `/`; here it is the heading below.
 * Every page carries exactly one, which is only possible because the header's
 * wordmark is a brand *link* rather than a heading — `site-header.tsx` says so
 * at the other end. `@/lib/heading-props` explains why the pair is spread from
 * a constant instead of written inline.
 *
 * ## The link is an anchor, not a button
 *
 * `Link` renders a real `<a>`, so it is focusable without help, announced as a
 * link, and openable in a new tab — none of which a press target gets for free.
 * Its text says where it goes on its own, so it survives being read out of
 * context in a screen reader's link list. The underline is stated rather than
 * left to the user agent: colour alone must not be the only thing marking a
 * link, and the padding around it both holds the focus ring off the letterforms
 * and carries the box past the 24 × 24 CSS px floor FR-019(g) sets.
 *
 * ## Layout
 *
 * The same arrangement `index.tsx` describes: a centred 1100 px column with no
 * inset of its own, wrapping a section that owns `spacing.md` on the horizontal
 * axis. That single gutter is what keeps a 320 px viewport reflowing without
 * horizontal scroll (FR-019(f)).
 *
 * There is no footer here. `role="main"` is therefore the page's only landmark
 * besides the header's `banner`, and it wraps everything below it.
 */

import type { CSSProperties } from "react";
import { Link } from "@tanstack/react-router";
import { StyleSheet, View } from "react-native";
import { Text } from "react-native-paper";
import { PageColumn } from "@/components/page-column";
import { headingProps } from "@/lib/heading-props";
import { useAppTheme } from "@/theme/paper-theme";
import { radius, spacing } from "@/theme/tokens";

const HEADLINE = "Page not found";

/**
 * One sentence. The old version offered a balanced pair of guesses about *why*
 * the page was missing, which reads as filling space — the visitor doesn't need
 * a theory, they need the way back.
 */
const EXPLANATION = "That address doesn't match anything on this site.";

const HOME_LINK_TEXT = "Back to the Sparkie home page";

/**
 * Prepared once and spread rather than written inline, because `aria-level` is
 * not in react-native's prop types — see `@/lib/heading-props`.
 */
const PAGE_HEADING_PROPS = headingProps(1);

/**
 * The anchor's own box, in DOM styles rather than a react-native-web
 * `StyleSheet`, for the same reason the header's brand link uses them: this
 * element really is an `<a>`. The theme colour is merged in at render, where
 * the theme can be read.
 */
const HOME_LINK_STYLE: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  borderRadius: radius.sm,
  padding: spacing.xs,
  textDecoration: "underline",
};

export function NotFound() {
  const theme = useAppTheme();

  return (
    <PageColumn>
      <View role="main" style={styles.section}>
        <Text
          variant="displaySmall"
          style={[styles.centered, { color: theme.colors.onSurface }]}
          {...PAGE_HEADING_PROPS}
        >
          {HEADLINE}
        </Text>

        <Text
          variant="bodyLarge"
          style={[styles.centered, { color: theme.colors.onSurfaceVariant }]}
        >
          {EXPLANATION}
        </Text>

        {/* The colour is set on the anchor as well as on the text it wraps:
            an underline is painted in the colour of the box that declares it,
            so leaving the anchor at the user agent's link blue would draw a
            blue rule under theme-coloured words. */}
        <Link
          to="/"
          style={{ ...HOME_LINK_STYLE, color: theme.colors.primary }}
        >
          <Text variant="titleMedium" style={{ color: theme.colors.primary }}>
            {HOME_LINK_TEXT}
          </Text>
        </Link>
      </View>
    </PageColumn>
  );
}

const styles = StyleSheet.create({
  section: {
    alignItems: "center",
    gap: spacing.lg,
    // Asymmetric like the hero's: the space above is the page's opening breath,
    // the space below only has to keep the link off the bottom of the window.
    paddingTop: spacing.xxxl,
    paddingBottom: spacing.xxl,
    paddingHorizontal: spacing.md,
  },
  // Paper's `Text` sets `textAlign: 'left'` on every variant, so centring the
  // wrapped lines has to be said explicitly.
  centered: {
    textAlign: "center",
  },
});
